import { useState, useEffect } from "react";
import { ShieldCheck, X } from "lucide-react";

interface Props {
  mode: "accept" | "view";
  onAccept?: () => void;
  onClose?: () => void;
}

export default function DataNoticeModal({ mode, onAccept, onClose }: Props) {
  const [aiModel, setAiModel] = useState<string | null>(null);
  const [deterministicSampling, setDeterministicSampling] = useState<boolean | null>(null);

  useEffect(() => {
    fetch("/api/public/config")
      .then((r) => r.json())
      .then((d: { aiModel?: string; deterministicSampling?: boolean }) => {
        if (d.aiModel) setAiModel(d.aiModel);
        if (typeof d.deterministicSampling === "boolean") setDeterministicSampling(d.deterministicSampling);
      })
      .catch(() => { /* non-fatal */ });
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-lg bg-card border rounded-xl shadow-xl overflow-y-auto max-h-[90vh]">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-5">
            <ShieldCheck className="h-6 w-6 text-primary shrink-0" />
            <h2 className="font-semibold text-lg flex-1">Data &amp; Privacy Notice</h2>
            {mode === "view" && (
              <button
                onClick={onClose}
                className="text-muted-foreground hover:text-foreground"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>

          <div className="space-y-4 text-sm text-muted-foreground">
            <p>
              This platform is operated by the{" "}
              <strong className="text-foreground">neuroXRehab COST Action WG1</strong> research consortium to
              manage living systematic reviews on XR-based rehabilitation for spatial neglect.
            </p>

            <div>
              <p className="font-semibold text-foreground mb-1">What we collect and store</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li>Your name and email address, used for login and the audit trail</li>
                <li>Screening decisions and data extraction records you submit</li>
                <li>Bibliographic records imported from literature databases</li>
              </ul>
            </div>

            <div>
              <p className="font-semibold text-foreground mb-1">AI-assisted processing</p>
              <p>
                When you request AI screening suggestions or extraction pre-fill, study text (titles,
                abstracts, or uploaded full-text PDFs) is sent to{" "}
                <strong className="text-foreground">Anthropic&apos;s Claude API</strong> for analysis.
                The specific model currently in use is{" "}
                <strong className="text-foreground">
                  {aiModel ?? "claude-sonnet-4-6"}
                </strong>
                .{" "}
                {deterministicSampling ? (
                  <>
                    To ensure reproducibility across repeated analyses, all AI calls use deterministic
                    settings (temperature = 0), meaning the same paper and criteria will produce the same
                    result when processed by the same model version. This is also why the platform is
                    deliberately kept on this model generation rather than a newer one: some newer Claude
                    models no longer support a fixed sampling temperature, which would break this
                    reproducibility guarantee. The platform will move to a newer model once Anthropic
                    provides an equivalent way to guarantee deterministic output.
                  </>
                ) : (
                  <>
                    This model does not support a fixed sampling temperature, so repeated analyses of the
                    same paper may produce minor wording differences even under identical criteria.
                  </>
                )}{" "}
                The exact model checkpoint used for
                each paper is recorded in the platform&apos;s audit trail. Anthropic states that data
                sent via the API is not used to train its models and is deleted within 30 days. See{" "}
                <a
                  href="https://www.anthropic.com/legal/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-foreground"
                >
                  Anthropic&apos;s Privacy Policy
                </a>{" "}
                (section: &ldquo;How we use your information&rdquo;) for full details.
              </p>
            </div>

            <div>
              <p className="font-semibold text-foreground mb-1">Who can see your data</p>
              <p>
                Your screening decisions and profile are visible to coordinators and administrators of this
                platform. Aggregated review results may appear in consortium publications; individual reviewer
                decisions are not published.
              </p>
            </div>

            <div>
              <p className="font-semibold text-foreground mb-1">Uploading published papers</p>
              <p>
                When you upload a PDF for AI-assisted screening or data extraction, the text from that paper
                is sent to Anthropic&apos;s Claude API to help analyse it. Please only upload papers you
                have legitimate access to, for example through your university library, an open-access
                journal, or your institution&apos;s subscription.
              </p>
              <p className="mt-1.5">
                Using published research as part of a systematic review is a recognised and accepted practice
                under copyright law in most countries, including EU member states, the UK, and the US. The
                text is being read and analysed for research purposes only; it is not being copied,
                republished, or shared publicly. Anthropic does not use submitted content to train its AI
                models and deletes it within 30 days.
              </p>
              <p className="mt-1.5">
                Where possible, please use open-access versions of papers. Do not upload content obtained
                through unauthorised means.
              </p>
            </div>

            <div>
              <p className="font-semibold text-foreground mb-1">Data storage</p>
              <p>
                All data is stored on the platform&apos;s own server. It is not shared with third parties
                beyond the Anthropic API calls described above.
              </p>
            </div>

            <div>
              <p className="font-semibold text-foreground mb-1">Your participation</p>
              <p>
                Participation as a reviewer is voluntary. You may request deletion of your account and
                associated data by contacting a platform administrator.
              </p>
            </div>
          </div>

          {mode === "accept" && (
            <button
              onClick={onAccept}
              className="mt-6 w-full py-2.5 px-4 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90"
            >
              I understand, continue
            </button>
          )}

          {mode === "view" && (
            <button
              onClick={onClose}
              className="mt-6 w-full py-2.5 px-4 border rounded-md text-sm font-medium hover:bg-accent"
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
